import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2, AlertCircle, Calculator } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatDeNumber } from '@/lib/utils';

const DEFAULT_TIERS = [0.00, 0.10, 0.20, 0.30, 0.40];
const DEFAULT_BY_COUNT = { 2: 0.05, 3: 0.05, 4: 0.07, 5: 0.10, 6: 0.10, 7: 0.10 };

function pctToFloat(v) {
  const n = Number(String(v).replace(',', '.'));
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n)) / 100;
}
function floatToPct(v) {
  return (Number(v) * 100).toFixed(0);
}

/**
 * Super-admin only. Lets the operator switch the rabatt scheme on a tenant
 * between three flavors:
 *   - position_tiers (Greven style): one % per family position
 *   - count_uniform (Bonn style): one % per total family size
 *   - custom (escape hatch): raw JSON
 *
 * The `two_stage` boolean is preserved across switches.
 */
export default function DiscountSchemeDialog({ open, onOpenChange, tenant, onSaved }) {
  const initialType = tenant?.discount_scheme?.type || 'position_tiers';
  const [tab, setTab] = useState(initialType === 'count_uniform' ? 'count_uniform' : initialType === 'custom' ? 'custom' : 'position_tiers');
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [byCount, setByCount] = useState(DEFAULT_BY_COUNT);
  const [fallback, setFallback] = useState(0.10);
  const [customJson, setCustomJson] = useState('');
  const [saving, setSaving] = useState(false);
  const [jsonErr, setJsonErr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Reset whenever a new tenant is opened
  useEffect(() => {
    if (!tenant) return;
    const sch = tenant.discount_scheme || {};
    if (sch.type === 'count_uniform') {
      setTab('count_uniform');
      setByCount(sch.by_count || DEFAULT_BY_COUNT);
      setFallback(Number(sch.fallback ?? 0.10));
    } else if (sch.type === 'position_tiers') {
      setTab('position_tiers');
      setTiers((sch.tiers && sch.tiers.length) ? sch.tiers.map(Number) : DEFAULT_TIERS);
    } else if (sch && Object.keys(sch).length > 0) {
      setTab('custom');
      setCustomJson(JSON.stringify(sch, null, 2));
    } else {
      setTab('position_tiers');
      setTiers(DEFAULT_TIERS);
    }
    setJsonErr(null);
  }, [tenant?.id, tenant?.discount_scheme]);

  const twoStage = useMemo(
    () => Boolean(tenant?.discount_scheme?.two_stage),
    [tenant?.discount_scheme],
  );

  const buildScheme = () => {
    if (tab === 'position_tiers') {
      return {
        type: 'position_tiers',
        tiers: tiers.map(Number),
        ...(twoStage ? { two_stage: true } : {}),
      };
    }
    if (tab === 'count_uniform') {
      const cleaned = {};
      Object.entries(byCount).forEach(([k, v]) => {
        cleaned[String(k)] = Number(v);
      });
      return {
        type: 'count_uniform',
        by_count: cleaned,
        fallback: Number(fallback),
        ...(twoStage ? { two_stage: true } : {}),
      };
    }
    // custom
    try {
      const parsed = JSON.parse(customJson || '{}');
      // preserve two_stage if not specified
      if (twoStage && !('two_stage' in parsed)) parsed.two_stage = true;
      return parsed;
    } catch (e) {
      setJsonErr(e.message);
      throw e;
    }
  };

  // Debounced preview: whenever the scheme draft changes, fetch deltas
  // against the current saved scheme. 400 ms debounce feels snappy without
  // hammering the backend on every keystroke.
  const previewSchemeString = useMemo(() => {
    try {
      return JSON.stringify(buildScheme());
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, tiers, byCount, fallback, customJson, twoStage]);

  useEffect(() => {
    if (!tenant?.id || !open || !previewSchemeString) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post(
          `/tenants/${tenant.id}/discount-scheme/preview`,
          { scheme: JSON.parse(previewSchemeString) },
        );
        if (!cancelled) setPreview(res.data);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [previewSchemeString, tenant?.id, open]);

  const handleSave = async () => {
    let scheme;
    try {
      scheme = buildScheme();
    } catch (e) {
      toast.error('JSON ungültig: ' + e.message);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.id}`, { discount_scheme: scheme });
      toast.success('Rabatt-Schema gespeichert');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefault = () => {
    setTab('position_tiers');
    setTiers(DEFAULT_TIERS);
  };

  if (!tenant) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="discount-scheme-dialog"
      >
        <DialogHeader>
          <DialogTitle>Rabatt-Schema · {tenant.name}</DialogTitle>
          <DialogDescription>
            Wählt das Familien-/Mehrfächer-Rabattmodell für diesen Mandanten.
            {twoStage && (
              <span className="ml-1 inline-flex items-center text-brand-red font-medium">
                Two-Stage ist aktiv – die Logik wird intra-Kind und inter-Kind
                in 2 Stufen angewendet.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="position_tiers" data-testid="scheme-tab-position">
              Position-Tiers
            </TabsTrigger>
            <TabsTrigger value="count_uniform" data-testid="scheme-tab-count">
              Count-Uniform
            </TabsTrigger>
            <TabsTrigger value="custom" data-testid="scheme-tab-custom">
              Custom JSON
            </TabsTrigger>
          </TabsList>

          {/* Position Tiers */}
          <TabsContent value="position_tiers" className="space-y-3 pt-4">
            <p className="text-sm text-slate-600">
              Greven-Stil: Jede Position in der nach Preis sortierten Familie
              erhält einen eigenen Rabatt. Position 0 = volle Gebühr.
            </p>
            <div className="space-y-2">
              {tiers.map((t, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Label className="w-24 text-xs text-slate-500">
                    {idx === 0 ? 'Höchstpreis' : `${idx + 1}.`}
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={floatToPct(t)}
                    onChange={(e) => {
                      const next = [...tiers];
                      next[idx] = pctToFloat(e.target.value);
                      setTiers(next);
                    }}
                    className="w-24 h-8 text-sm font-mono"
                    data-testid={`scheme-tier-input-${idx}`}
                  />
                  <span className="text-sm text-slate-500">%</span>
                  {idx > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 h-7 px-2"
                      onClick={() => setTiers(tiers.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setTiers([...tiers, tiers[tiers.length - 1] || 0])}
                data-testid="scheme-tier-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Stufe hinzufügen
              </Button>
            </div>
            <div className="text-xs text-slate-500 pt-2">
              Beispiel-Greven: 0 / 10 / 20 / 30 % (Position 0 zahlt voll, ab
              Position 4 dauerhaft 30 %).
            </div>
          </TabsContent>

          {/* Count Uniform */}
          <TabsContent value="count_uniform" className="space-y-3 pt-4">
            <p className="text-sm text-slate-600">
              Bonn-Stil: Ein einheitlicher Rabatt-Satz pro Familiengröße. Die
              höchste Belegung zahlt immer voll, alle weiteren erhalten den
              Satz. Fällt eine Größe nicht in die Tabelle, gilt der Fallback.
            </p>
            <div className="space-y-2">
              {Object.entries(byCount).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <Label className="w-24 text-xs text-slate-500">
                    {k} Familienmitglieder
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={floatToPct(v)}
                    onChange={(e) => setByCount({ ...byCount, [k]: pctToFloat(e.target.value) })}
                    className="w-24 h-8 text-sm font-mono"
                    data-testid={`scheme-bycount-input-${k}`}
                  />
                  <span className="text-sm text-slate-500">%</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 h-7 px-2"
                    onClick={() => {
                      const { [k]: _, ...rest } = byCount;
                      setByCount(rest);
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const keys = Object.keys(byCount).map(Number);
                  const next = (keys.length ? Math.max(...keys) : 1) + 1;
                  setByCount({ ...byCount, [next]: fallback });
                }}
                data-testid="scheme-bycount-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Größe hinzufügen
              </Button>
              <div className="flex items-center gap-2 pt-3 border-t border-slate-100 mt-3">
                <Label className="w-24 text-xs text-slate-500">Fallback</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={floatToPct(fallback)}
                  onChange={(e) => setFallback(pctToFloat(e.target.value))}
                  className="w-24 h-8 text-sm font-mono"
                  data-testid="scheme-fallback-input"
                />
                <span className="text-sm text-slate-500">% für übrige Größen</span>
              </div>
            </div>
          </TabsContent>

          {/* Custom JSON */}
          <TabsContent value="custom" className="space-y-3 pt-4">
            <p className="text-sm text-slate-600">
              Escape-Hatch für Spezialfälle. Erlaubte Top-Level-Keys: <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">type</code>,
              <code className="text-xs bg-slate-100 px-1 py-0.5 rounded ml-1">tiers</code>,
              <code className="text-xs bg-slate-100 px-1 py-0.5 rounded ml-1">by_count</code>,
              <code className="text-xs bg-slate-100 px-1 py-0.5 rounded ml-1">fallback</code>,
              <code className="text-xs bg-slate-100 px-1 py-0.5 rounded ml-1">two_stage</code>.
            </p>
            <textarea
              data-testid="scheme-custom-json"
              value={customJson}
              onChange={(e) => { setCustomJson(e.target.value); setJsonErr(null); }}
              className="w-full h-56 font-mono text-xs border border-slate-200 rounded p-3 focus:outline-none focus:ring-2 focus:ring-brand-red/30"
              placeholder='{"type":"position_tiers","tiers":[0,0.1,0.2,0.3]}'
            />
            {jsonErr && (
              <div className="flex items-start gap-2 text-xs text-red-600">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>JSON-Fehler: {jsonErr}</span>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Live Preview: sample-family deltas against the saved scheme */}
        <div
          className="mt-4 border border-brand-red/10 rounded-lg bg-brand-red/5 p-3"
          data-testid="scheme-preview-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-brand-red">
              <Calculator className="w-4 h-4" />
              Live-Rechner · Auswirkung auf Beispielfamilien
            </div>
            {previewLoading && (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-red" />
            )}
          </div>
          {preview?.samples?.length ? (
            <div className="overflow-hidden rounded border border-brand-red/10 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-brand-red/10 text-brand-red">
                  <tr>
                    <th className="text-left px-2 py-1.5">Szenario</th>
                    <th className="text-right px-2 py-1.5">Volltarif</th>
                    <th className="text-right px-2 py-1.5">Ist</th>
                    <th className="text-right px-2 py-1.5">Neu</th>
                    <th className="text-right px-2 py-1.5">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.samples.map((s, i) => {
                    const deltaClr = s.delta > 0
                      ? 'text-rose-600 font-semibold'
                      : s.delta < 0
                        ? 'text-emerald-600 font-semibold'
                        : 'text-slate-400';
                    return (
                      <tr
                        key={i}
                        className="border-t border-brand-red/5"
                        data-testid={`scheme-preview-row-${i}`}
                      >
                        <td className="px-2 py-1.5">
                          <div className="font-medium text-slate-800">{s.name}</div>
                          <div className="text-[10px] text-slate-500">
                            {s.description}
                          </div>
                        </td>
                        <td className="text-right font-mono text-slate-500 px-2 py-1.5">
                          {formatDeNumber(s.base_sum)} €
                        </td>
                        <td className="text-right font-mono text-slate-700 px-2 py-1.5">
                          {formatDeNumber(s.current_total)} €
                        </td>
                        <td className="text-right font-mono text-brand-red font-semibold px-2 py-1.5">
                          {formatDeNumber(s.preview_total)} €
                        </td>
                        <td className={`text-right font-mono px-2 py-1.5 ${deltaClr}`}>
                          {s.delta > 0 ? '+' : ''}{formatDeNumber(s.delta)} €
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">
              {jsonErr
                ? 'Preview wartet auf gültiges JSON…'
                : 'Lade Preview…'}
            </p>
          )}
          <p className="mt-2 text-[10px] text-slate-500">
            Synthetische Beispielfamilien · nur Monats-Grundgebühren, ohne
            GEMA, 14-tägig-Faktor oder Verwaltungspauschale.
          </p>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 pt-4">
          <Button
            variant="ghost"
            onClick={handleResetDefault}
            data-testid="scheme-reset-default"
          >
            Auf Greven-Default zurücksetzen
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="scheme-save"
              className="bg-brand-red hover:bg-brand-red/90 text-white"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Speichern
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
